import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';

import { usersSearchQueryOptions } from '../../api/users';
import { type User } from '../../types';
import { parseOUs } from '../../utils/dn';
import { UserAvatar } from '../photo/UserAvatar';
import { UserCard } from './UserCard';

function UserOUDisplay({ dn }: { dn: string | null | undefined }) {
  const ous = parseOUs(dn);
  if (ous.length === 0) {
    return null;
  }
  return (
    <p className="mono truncate text-xs text-muted-soft">{ous.join(' › ')}</p>
  );
}

// Highlight the matched substring of `query` inside `text` (case-insensitive).
function HighlightMatch({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (q.length === 0) {
    return <>{text}</>;
  }
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i === -1) {
    return <>{text}</>;
  }
  return (
    <>
      {text.slice(0, i)}
      <span className="bg-green/35 text-ink">
        {text.slice(i, i + q.length)}
      </span>
      {text.slice(i + q.length)}
    </>
  );
}

interface UserSearchProps {
  onSelectUser: (user: User | null) => void;
  selectedUser: User | null;
}

export function UserSearch({ onSelectUser, selectedUser }: UserSearchProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // The fetch itself is gated by `enabled: q.length >= 2` inside
  // usersSearchQueryOptions — React Query won't call the API for a 1-char term.
  // It also gives us 30s caching and cancels superseded requests, so fast
  // typing no longer stacks calls.
  const {
    data: results = [],
    isFetching,
    error,
  } = useQuery(usersSearchQueryOptions(debouncedQuery));
  const errorMessage = error instanceof Error ? error.message : null;

  // The 300ms debounce lives here: it feeds the (trimmed) input into the query
  // key. The `length >= 2` check below is NOT the request gate (that's the
  // `enabled` above) — it only decides whether to open the dropdown, so typing
  // a single character doesn't pop open an empty menu.
  const applyQuery = useDebouncedCallback((value: string) => {
    const trimmed = value.trim();
    setDebouncedQuery(trimmed);
    if (trimmed.length >= 2) {
      setIsOpen(true);
    }
  }, 300);

  // Clearing the input drops the search at once, and any keystroke still
  // waiting on the debounce with it.
  function clearQuery() {
    setQuery('');
    applyQuery.cancel();
    setDebouncedQuery('');
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        event.target instanceof Node &&
        !wrapperRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(user: User) {
    onSelectUser(user);
    // Clear query plutôt que de la mettre au displayName : sinon la recherche
    // repartirait et rouvrirait le dropdown. L'input n'est pas affiché tant que
    // selectedUser est posé, donc la valeur n'est pas visible — autant repartir
    // vide pour une prochaine désélection.
    clearQuery();
    setIsOpen(false);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setQuery(value);
    applyQuery(value);
    if (value.trim().length === 0) {
      setIsOpen(false);
    }
  }

  return (
    <div ref={wrapperRef} className="relative w-full">
      <label htmlFor={inputId} className="label mb-1 block">
        {t('userSearch.placeholder')}
      </label>
      <div className="relative">
        {selectedUser ? (
          <div className="relative">
            <UserCard user={selectedUser} />
            <button
              onClick={() => {
                clearQuery();
                onSelectUser(null);
              }}
              className="absolute top-2 right-2 p-1 text-muted transition-colors"
              title={t('userSearch.clearSelection')}
            >
              <svg
                className="size-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        ) : (
          <input
            id={inputId}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => results.length > 0 && setIsOpen(true)}
            placeholder={t('userSearch.hint')}
            className="w-full px-4 py-2.5"
          />
        )}
        {!selectedUser && isFetching && (
          <div className="absolute top-1/2 right-3 -translate-y-1/2">
            <svg
              className="size-5 animate-spin text-blue"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          </div>
        )}
      </div>

      {errorMessage && (
        <p className="mt-1 text-sm text-magenta">{errorMessage}</p>
      )}

      {isOpen && results.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto border border-ink bg-card">
          {results.map((user) => (
            <li
              key={user.id}
              className="border-t border-t-rule first:border-t-0"
            >
              <button
                onClick={() => handleSelect(user)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-blue/5"
              >
                <UserAvatar
                  userId={user.id}
                  displayName={user.displayName || user.userPrincipalName}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">
                    <HighlightMatch text={user.displayName} query={query} />
                  </p>
                  <p className="mono truncate text-xs text-muted">
                    <HighlightMatch
                      text={user.userPrincipalName}
                      query={query}
                    />
                  </p>
                  <UserOUDisplay dn={user.onPremisesDistinguishedName} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {isOpen &&
        !isFetching &&
        results.length === 0 &&
        debouncedQuery.length >= 2 && (
          <div className="absolute z-10 mt-1 w-full border border-ink bg-card px-4 py-3 text-sm text-muted">
            {t('userSearch.noResults', { query })}
          </div>
        )}
    </div>
  );
}
